import vehiclesData from '../data/vehicles.json';
import alertsData from '../data/alerts.json';
import thresholdsData from '../data/thresholds.json';
import { 
  generateHistoricalData, 
  generateTemperatureReading,
  initializeVehicleReadings,
  generateAlert
} from './dataGenerator';

// Simulate network delay
const delay = (ms = 200) => new Promise(resolve => setTimeout(resolve, ms));

// In-memory storage for readings and alerts
let temperatureReadings = {};
let alerts = [...alertsData.alerts];
let settings = {
  theme: 'light',
  temperatureUnit: 'C',
  alertSensitivity: 'medium',
  soundEnabled: true,
  emailAlerts: false,
  email: '',
};

// Initialize readings for all vehicles
const initializeReadings = () => {
  vehiclesData.vehicles.forEach(vehicle => {
    // Generate data for the last 7 days with 30-minute intervals
    const now = new Date();
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    temperatureReadings[vehicle.id] = generateHistoricalData(vehicle.id, sevenDaysAgo, now, 30);
  });
};

// Initialize data on module load
initializeReadings();

/**
 * Mock API Service
 */
export const mockApi = {
  // Vehicles
  async getVehicles() {
    await delay();
    return { data: vehiclesData.vehicles, error: null };
  },
  
  async getVehicle(vehicleId) {
    await delay();
    const vehicle = vehiclesData.vehicles.find(v => v.id === vehicleId);
    return vehicle 
      ? { data: vehicle, error: null }
      : { data: null, error: 'Vehicle not found' };
  },
  
  // Temperature Readings
  async getTemperatureReadings(vehicleId, startDate, endDate) {
    await delay(300);
    
    if (!temperatureReadings[vehicleId]) {
      return { data: [], error: 'No readings found for vehicle' };
    }
    
    let readings = temperatureReadings[vehicleId];
    
    console.log('mockApi - Total readings for vehicle:', readings.length);
    console.log('mockApi - Date range:', { startDate, endDate });
    
    // Filter by date range if provided
    if (startDate) {
      const start = new Date(startDate);
      readings = readings.filter(r => new Date(r.timestamp) >= start);
      console.log('mockApi - After start filter:', readings.length);
    }
    
    if (endDate) {
      const end = new Date(endDate);
      readings = readings.filter(r => new Date(r.timestamp) <= end);
      console.log('mockApi - After end filter:', readings.length);
    }
    
    if (readings.length > 0) {
      console.log('mockApi - First reading:', readings[0].timestamp);
      console.log('mockApi - Last reading:', readings[readings.length - 1].timestamp);
    }
    
    return { data: readings, error: null };
  },
  
  async getCurrentReading(vehicleId) {
    await delay(100);
    
    if (!temperatureReadings[vehicleId] || temperatureReadings[vehicleId].length === 0) {
      return { data: null, error: 'No readings found' };
    }
    
    const readings = temperatureReadings[vehicleId];
    return { data: readings[readings.length - 1], error: null };
  },
  
  async getAllCurrentReadings() {
    await delay(150);
    
    const currentReadings = {};
    
    vehiclesData.vehicles.forEach(vehicle => {
      const readings = temperatureReadings[vehicle.id];
      if (readings && readings.length > 0) {
        currentReadings[vehicle.id] = readings[readings.length - 1];
      }
    });
    
    return { data: currentReadings, error: null };
  },
  
  // Alerts
  async getAlerts(filters = {}) {
    await delay();
    
    let filteredAlerts = [...alerts];
    
    // Apply filters
    if (filters.severity) {
      filteredAlerts = filteredAlerts.filter(a => a.severity === filters.severity);
    }
    
    if (filters.vehicleId) {
      filteredAlerts = filteredAlerts.filter(a => a.vehicleId === filters.vehicleId);
    }
    
    if (filters.acknowledged !== undefined) {
      filteredAlerts = filteredAlerts.filter(a => a.acknowledged === filters.acknowledged);
    }
    
    if (filters.resolved !== undefined) {
      filteredAlerts = filteredAlerts.filter(a => a.resolved === filters.resolved);
    }
    
    // Sort by timestamp (most recent first)
    filteredAlerts.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    
    return { data: filteredAlerts, error: null };
  },
  
  async acknowledgeAlert(alertId, acknowledgedBy = 'System User') {
    await delay();
    
    const alert = alerts.find(a => a.id === alertId);
    if (!alert) {
      return { data: null, error: 'Alert not found' };
    }
    
    alert.acknowledged = true;
    alert.acknowledgedBy = acknowledgedBy;
    alert.acknowledgedAt = new Date().toISOString();
    
    return { data: alert, error: null };
  },
  
  async resolveAlert(alertId, notes = '') {
    await delay();
    
    const alert = alerts.find(a => a.id === alertId);
    if (!alert) {
      return { data: null, error: 'Alert not found' };
    }
    
    alert.resolved = true;
    alert.resolvedAt = new Date().toISOString();
    alert.notes = notes;
    
    return { data: alert, error: null };
  },
  
  async createAlert(alertData) {
    await delay();
    
    const newAlert = {
      id: `ALT-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      ...alertData,
      timestamp: alertData.timestamp || new Date().toISOString(),
      acknowledged: false,
      acknowledgedBy: null,
      acknowledgedAt: null,
      resolved: false,
      resolvedAt: null,
      notes: '',
    };
    
    alerts.unshift(newAlert);
    
    return { data: newAlert, error: null };
  },
  
  // Settings
  async getSettings() {
    await delay();
    return { data: { ...settings }, error: null };
  },
  
  async updateSettings(newSettings) {
    await delay();
    settings = { ...settings, ...newSettings };
    
    // Save to localStorage
    try {
      localStorage.setItem('temperatureMonitoringSettings', JSON.stringify(settings));
    } catch (e) {
      console.warn('Failed to save settings to localStorage', e);
    }
    
    return { data: settings, error: null };
  },
  
  // Thresholds
  async getThresholds(vehicleId) {
    await delay();
    
    const thresholds = vehicleId && thresholdsData.customThresholds[vehicleId]
      ? thresholdsData.customThresholds[vehicleId]
      : thresholdsData.default;
    
    return { data: thresholds, error: null };
  },
  
  async updateThresholds(vehicleId, newThresholds) {
    await delay();
    
    if (vehicleId) {
      thresholdsData.customThresholds[vehicleId] = { ...newThresholds };
    } else {
      thresholdsData.default = { ...newThresholds };
    }
    
    return { data: newThresholds, error: null };
  },
};

/**
 * Simulate real-time updates
 * Call this function periodically to generate new readings
 */
export const simulateRealtimeUpdate = () => {
  vehiclesData.vehicles.forEach(vehicle => {
    if (!temperatureReadings[vehicle.id]) {
      temperatureReadings[vehicle.id] = [];
    }
    
    const readings = temperatureReadings[vehicle.id];
    const lastReading = readings.length > 0 ? readings[readings.length - 1] : null;
    const previousTemp = lastReading ? lastReading.temperature : null;
    
    const newReading = generateTemperatureReading(vehicle.id, previousTemp);
    readings.push(newReading);
    
    // Keep only last 7 days of data
    const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    temperatureReadings[vehicle.id] = readings.filter(
      r => new Date(r.timestamp) >= sevenDaysAgo
    );
    
    // Check if we should generate an alert
    if (newReading.status === 'critical' || newReading.status === 'warning') {
      // 30% chance to create alert for warning/critical readings
      if (Math.random() < 0.3) {
        const alert = generateAlert(vehicle.id, newReading.temperature);
        alerts.unshift(alert);
        
        // Limit alerts to last 1000
        if (alerts.length > 1000) {
          alerts = alerts.slice(0, 1000);
        }
      }
    }
  });
};

/**
 * Start real-time simulation
 * @param {number} interval - Update interval in milliseconds
 * @returns {number} - Interval ID
 */
export const startRealtimeSimulation = (interval = 10000) => {
  return setInterval(simulateRealtimeUpdate, interval);
};

/**
 * Stop real-time simulation
 * @param {number} intervalId - Interval ID to clear
 */
export const stopRealtimeSimulation = (intervalId) => {
  clearInterval(intervalId);
};

// Load settings from localStorage on init
try {
  const savedSettings = localStorage.getItem('temperatureMonitoringSettings');
  if (savedSettings) {
    settings = { ...settings, ...JSON.parse(savedSettings) };
  }
} catch (e) {
  console.warn('Failed to load settings from localStorage', e);
}

